import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, ArrowRight, AlertCircle } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TRIES = 8;

const PaymentSuccess = () => {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [ref, setRef] = useState(null);

  const poll = useCallback(
    async (tries) => {
      if (!sessionId) {
        setState("error");
        return;
      }
      if (tries >= MAX_TRIES) {
        setState("timeout");
        return;
      }
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.ref) setRef(res.data.ref);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) {
          setState("error");
          return;
        }
        setTimeout(() => poll(tries + 1), 2000);
      } catch (e) {
        setTimeout(() => poll(tries + 1), 2000);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  const copy = {
    fr: {
      polling: "Vérification de votre paiement…",
      paidTitle: "Acompte reçu, merci !",
      paidText:
        "Votre réservation a bien été enregistrée avec le paiement de l'acompte. Nous vérifions la disponibilité auprès du prestataire et revenons vers vous très vite pour confirmer votre réservation.",
      remind:
        "Rappel : cette réservation reste soumise à l'acceptation et à la disponibilité du prestataire.",
      timeoutTitle: "Paiement en cours de traitement",
      timeoutText:
        "Votre paiement est en cours de confirmation. Vous recevrez un message dès qu'il est validé.",
      errorTitle: "Un problème est survenu",
      errorText: "Nous n'avons pas pu confirmer votre paiement. Merci de nous contacter.",
      home: "Retour à l'accueil",
      activities: "Voir les activités",
      refLabel: "Votre numéro de réservation",
      refHint: "Un reçu vous a été envoyé par email. Conservez ce numéro.",
    },
    en: {
      polling: "Verifying your payment…",
      paidTitle: "Deposit received, thank you!",
      paidText:
        "Your booking has been saved together with the deposit payment. We're checking availability with the provider and will get back to you shortly to confirm your booking.",
      remind:
        "Reminder: this booking remains subject to the provider's acceptance and availability.",
      timeoutTitle: "Payment being processed",
      timeoutText:
        "Your payment is being confirmed. You'll receive a message as soon as it is validated.",
      errorTitle: "Something went wrong",
      errorText: "We couldn't confirm your payment. Please contact us.",
      home: "Back to home",
      activities: "View activities",
      refLabel: "Your booking number",
      refHint: "A receipt has been emailed to you. Keep this number.",
    },
  }[lang];

  return (
    <div className="min-h-screen bg-[#f7f5ef] flex items-center justify-center px-5 pt-24 pb-16">
      <div className="max-w-lg w-full bg-white rounded-3xl border border-[#eceae1] p-10 md:p-12 text-center shadow-[0_25px_60px_-30px_rgba(28,34,30,0.35)]">
        {state === "polling" && (
          <>
            <Loader2 size={44} className="text-[#7c8f74] mx-auto mb-6 animate-spin" />
            <p className="text-[#6b7166] text-lg">{copy.polling}</p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#7c8f74] mx-auto flex items-center justify-center mb-6">
              <Check size={32} className="text-white" />
            </div>
            <h1 className="font-serif text-4xl text-[#20261f] mb-4">{copy.paidTitle}</h1>
            <p className="text-[#6b7166] leading-relaxed mb-5">{copy.paidText}</p>
            {ref && (
              <div className="bg-[#20261f] rounded-xl px-5 py-4 mb-5">
                <p className="text-[11px] uppercase tracking-[0.15em] text-[#b6c3ab] mb-1">{copy.refLabel}</p>
                <p className="font-serif text-3xl text-white tracking-wide">{ref}</p>
                <p className="text-[11px] text-white/50 mt-2">{copy.refHint}</p>
              </div>
            )}
            <p className="text-[13px] text-[#9aa08f] bg-[#eef0e9] rounded-lg px-4 py-3 mb-8">{copy.remind}</p>
          </>
        )}

        {(state === "timeout" || state === "error") && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#e7e4d9] mx-auto flex items-center justify-center mb-6">
              <AlertCircle size={30} className="text-[#5c6b54]" />
            </div>
            <h1 className="font-serif text-3xl text-[#20261f] mb-4">
              {state === "timeout" ? copy.timeoutTitle : copy.errorTitle}
            </h1>
            <p className="text-[#6b7166] leading-relaxed mb-8">
              {state === "timeout" ? copy.timeoutText : copy.errorText}
            </p>
          </>
        )}

        {state !== "polling" && (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 border border-[#dedbd0] text-[#3d443a] px-6 py-3 rounded-full text-sm tracking-wide hover:border-[#7c8f74] transition"
            >
              {copy.home}
            </Link>
            <Link
              to="/activites"
              className="inline-flex items-center justify-center gap-2 bg-[#7c8f74] hover:bg-[#697c62] text-white px-6 py-3 rounded-full text-sm tracking-wide transition"
            >
              {copy.activities} <ArrowRight size={15} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
